// Gestiona carga de datos, cache local y búsqueda de destinos/hoteles.
import Fuse from "fuse.js";

const TOKEN_SEARCH_MIN_LENGTH = 3;
const TOKEN_ONLY_SCORE_PENALTY = 0.35;
const SEARCH_DATA_CACHE_KEY_PREFIX = "fb-lite:search-data:v1";
const SEARCH_DATA_CACHE_TTL_MS = 30 * 60 * 1000;

const normalizeForSearch = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const escapeHtml = (value) =>
  String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const mergeIntervals = (indices = []) => {
  if (!indices.length) {
    return [];
  }
  const sorted = [...indices].sort((a, b) => a[0] - b[0]);
  return sorted.reduce((acc, current) => {
    if (!acc.length) {
      return [current];
    }
    const prev = acc.at(-1);
    if (current[0] <= prev[1] + 1) {
      prev[1] = Math.max(prev[1], current[1]);
    } else {
      acc.push(current);
    }
    return acc;
  }, []);
};
const createDataSearchService = (host) => {
  const service = {
    normalizeForSearch,
    escapeHtml,

    buildFuseKeysWithNormalizedVariants(keys = []) {
      const result = [];
      const seen = new Set();

      keys.forEach((keyDef) => {
        const isObj = typeof keyDef === "object" && keyDef !== null;
        const name = isObj ? keyDef.name : keyDef;

        if (!name || seen.has(name)) {
          return;
        }
        result.push(keyDef);
        seen.add(name);

        if (!name.endsWith("Normalized")) {
          const normName = `${name}Normalized`;
          if (!seen.has(normName)) {
            result.push(isObj ? { ...keyDef, name: normName } : normName);
            seen.add(normName);
          }
        }
      });

      return result;
    },

    getSearchTokens(query) {
      const normalizedQuery = normalizeForSearch(query);
      if (!normalizedQuery) {
        return [];
      }

      const tokens = normalizedQuery.split(/\s+/).filter((token) => token.length >= TOKEN_SEARCH_MIN_LENGTH && token !== normalizedQuery);

      return [...new Set(tokens)];
    },

    collectMatchIndices(byKey, match) {
      const key = match?.key;
      if (!key) {
        return;
      }

      const previousIndices = byKey.get(key) || [];
      byKey.set(key, previousIndices.concat(match.indices || []));
    },

    toMergedMatchEntry(key, rawIndices = []) {
      const validIndices = rawIndices.filter((pair) => pair?.length === 2);

      return {
        key,
        indices: mergeIntervals(validIndices),
      };
    },

    mergeMatches(existingMatches = [], nextMatches = []) {
      const merged = [...existingMatches, ...nextMatches];
      if (!merged.length) {
        return [];
      }

      const byKey = new Map();
      merged.forEach((match) => {
        service.collectMatchIndices(byKey, match);
      });

      return Array.from(byKey.entries()).map(([key, rawIndices]) => service.toMergedMatchEntry(key, rawIndices));
    },

    mergeSearchResults(primaryResults = [], tokenResults = []) {
      const mergedResults = [];
      const indexById = new Map();

      primaryResults.forEach((result, index) => {
        if (!result?.item?._id) {
          return;
        }
        mergedResults.push({ ...result, _sortIndex: index, _isPrimary: true });
        indexById.set(result.item._id, mergedResults.length - 1);
      });

      tokenResults.forEach((result, tokenIndex) => {
        const id = result?.item?._id;
        if (!id) {
          return;
        }

        const tokenOrder = result?._tokenOrder ?? 0;
        const tokenScore = (result?.score ?? Number.POSITIVE_INFINITY) + TOKEN_ONLY_SCORE_PENALTY + tokenOrder * 0.15;
        const existingIndex = indexById.get(id);

        if (existingIndex === undefined) {
          mergedResults.push({
            ...result,
            score: tokenScore,
            _sortIndex: primaryResults.length + tokenIndex,
            _isPrimary: false,
          });
          indexById.set(id, mergedResults.length - 1);
          return;
        }

        const existing = mergedResults[existingIndex];
        existing.matches = service.mergeMatches(existing.matches, result.matches);

        if (!existing._isPrimary && tokenScore < existing.score) {
          existing.score = tokenScore;
          existing._sortIndex = Math.min(existing._sortIndex, primaryResults.length + tokenIndex);
        }
      });

      return mergedResults.sort((a, b) => {
        if (a._isPrimary !== b._isPrimary) {
          return a._isPrimary ? -1 : 1;
        }
        const aScore = a.score ?? Number.POSITIVE_INFINITY;
        const bScore = b.score ?? Number.POSITIVE_INFINITY;

        if (aScore !== bScore) {
          return aScore - bScore;
        }
        return a._sortIndex - b._sortIndex;
      });
    },

    ensureSearchDataLoaded(fastbooking) {
      if (host.props.sourceTree?.length) {
        return Promise.resolve(host.props.searchData);
      }
      if (host.props.loadingSearchDataPromise) {
        return host.props.loadingSearchDataPromise;
      }

      host.props.loadingSearchDataPromise = service.fetchSearchData(fastbooking).finally(() => {
        host.props.loadingSearchDataPromise = null;
      });

      return host.props.loadingSearchDataPromise;
    },

    normalizeMeetingDetails(payload = {}) {
      const normalizeList = (rawValues) => {
        if (!Array.isArray(rawValues)) {
          return [];
        }

        const uniqueValues = new Set();
        rawValues.forEach((value) => {
          const normalizedValue = String(value || "").trim();
          if (normalizedValue) {
            uniqueValues.add(normalizedValue);
          }
        });

        return Array.from(uniqueValues).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
      };

      return {
        eventTypes: normalizeList(payload?.eventTypes),
        capacities: normalizeList(payload?.capacities),
      };
    },

    normalizeMeetingEventTypesHint(rawValues) {
      const values = Array.isArray(rawValues) ? rawValues : String(rawValues || "").split(",");
      const normalizedValues = new Set();

      values.forEach((value) => {
        const normalizedValue = String(value || "").trim();
        if (normalizedValue) {
          normalizedValues.add(normalizedValue);
        }
      });

      return Array.from(normalizedValues).sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }));
    },

    mergeMeetingDetailsWithHint(meetingDetails, eventTypesHint = []) {
      const normalizedDetails = service.normalizeMeetingDetails(meetingDetails);
      const normalizedHint = service.normalizeMeetingEventTypesHint(eventTypesHint);

      if (!normalizedHint.length) {
        return normalizedDetails;
      }

      return {
        ...normalizedDetails,
        eventTypes: normalizedHint,
      };
    },

    buildMeetingDetailsUrl(componentPath, meetingPath, eventTypesHint = []) {
      const params = new URLSearchParams({ meetingPath });
      const normalizedHint = service.normalizeMeetingEventTypesHint(eventTypesHint);
      if (normalizedHint.length) {
        params.set("eventTypes", normalizedHint.join(","));
      }

      return `${componentPath}?${params.toString()}`;
    },

    resolveLoaderContainer(fastbooking) {
      if (!fastbooking) {
        return null;
      }

      const destinationModal = fastbooking.querySelector(".fb-lite-modal-JS.mod--destinationModal");
      return destinationModal || fastbooking;
    },

    canToggleBhgLoader() {
      return typeof bcl !== "undefined" && bcl.u && typeof bcl.u.addBhgLoading === "function" && typeof bcl.u.removeBhgLoading === "function";
    },

    setBhgLoaderState(loaderContainer, shouldShow) {
      if (!loaderContainer || !service.canToggleBhgLoader()) {
        return;
      }

      if (shouldShow) {
        bcl.u.addBhgLoading(loaderContainer);
        return;
      }

      bcl.u.removeBhgLoading(loaderContainer);
    },

    createTimedAjaxRequestPromise({ requestUrl, timeoutMs = 10000, timeoutErrorMessage = "Timeout: La petición tardó demasiado o falló silenciosamente.", emptyResponseErrorMessage = "Empty response", onEmptyResponse, transformPayload }) {
      return new Promise((resolve, reject) => {
        const fallbackTimer = setTimeout(() => {
          reject(new Error(timeoutErrorMessage));
        }, timeoutMs);

        bcl.ajax.getRequest(
          null,
          requestUrl,
          (request) => {
            clearTimeout(fallbackTimer);

            try {
              const requestStatus = request?.status;
              if (Number.isFinite(requestStatus) && (requestStatus < 200 || requestStatus >= 400)) {
                return reject(new Error(`HTTP error: ${requestStatus}`));
              }

              const rawPayload = request?.response ?? request?.responseText;
              if (!rawPayload) {
                if (typeof onEmptyResponse === "function") {
                  return resolve(onEmptyResponse());
                }

                return reject(new Error(emptyResponseErrorMessage));
              }

              const parsedPayload = typeof rawPayload === "string" ? JSON.parse(rawPayload) : rawPayload;
              resolve(typeof transformPayload === "function" ? transformPayload(parsedPayload) : parsedPayload);
            } catch (parseError) {
              reject(parseError);
            }
          },
          null,
          false,
          (_, failedRequest) => {
            clearTimeout(fallbackTimer);
            reject(new Error(`HTTP error: ${failedRequest?.status || "unknown"}`));
          },
        );
      });
    },

    getCachedMeetingDetails(normalizedMeetingPath, normalizedEventTypesHint) {
      if (!host.props.meetingDetailsByPath?.[normalizedMeetingPath]) {
        return null;
      }

      return service.mergeMeetingDetailsWithHint(host.props.meetingDetailsByPath[normalizedMeetingPath], normalizedEventTypesHint);
    },

    getInFlightMeetingDetails(normalizedMeetingPath, normalizedEventTypesHint) {
      if (!host.props.meetingDetailsRequestsByPath?.[normalizedMeetingPath]) {
        return null;
      }

      return host.props.meetingDetailsRequestsByPath[normalizedMeetingPath].then((meetingDetails) => {
        const mergedMeetingDetails = service.mergeMeetingDetailsWithHint(meetingDetails, normalizedEventTypesHint);
        if (normalizedEventTypesHint.length) {
          host.props.meetingDetailsByPath[normalizedMeetingPath] = mergedMeetingDetails;
        }
        return mergedMeetingDetails;
      });
    },

    createMeetingDetailsRequestPromise(requestUrl) {
      return service.createTimedAjaxRequestPromise({
        requestUrl,
        timeoutMs: 10000,
        timeoutErrorMessage: "Timeout: room-details no respondió a tiempo.",
        onEmptyResponse: () => service.normalizeMeetingDetails(),
        transformPayload: (payload) => service.normalizeMeetingDetails(payload),
      });
    },

    async fetchMeetingDetails(fastbooking, meetingPath, eventTypesHint = []) {
      const normalizedMeetingPath = String(meetingPath || "").trim();
      const normalizedEventTypesHint = service.normalizeMeetingEventTypesHint(eventTypesHint);
      const endpoint = fastbooking?.dataset?.meetingdetail;

      if (!endpoint || !normalizedMeetingPath) {
        return null;
      }

      const cachedMeetingDetails = service.getCachedMeetingDetails(normalizedMeetingPath, normalizedEventTypesHint);
      if (cachedMeetingDetails) {
        return cachedMeetingDetails;
      }

      const inFlightMeetingDetails = service.getInFlightMeetingDetails(normalizedMeetingPath, normalizedEventTypesHint);
      if (inFlightMeetingDetails) {
        return inFlightMeetingDetails;
      }

      const requestUrl = service.buildMeetingDetailsUrl(endpoint, normalizedMeetingPath, normalizedEventTypesHint);
      const loaderContainer = service.resolveLoaderContainer(fastbooking);
      service.setBhgLoaderState(loaderContainer, true);

      const requestPromise = service.createMeetingDetailsRequestPromise(requestUrl);

      host.props.meetingDetailsRequestsByPath[normalizedMeetingPath] = requestPromise;

      try {
        const meetingDetails = await requestPromise;
        const mergedMeetingDetails = service.mergeMeetingDetailsWithHint(meetingDetails, normalizedEventTypesHint);
        host.props.meetingDetailsByPath[normalizedMeetingPath] = mergedMeetingDetails;
        return mergedMeetingDetails;
      } catch (error) {
        console.error("Error fetching meeting details:", error);
        return null;
      } finally {
        delete host.props.meetingDetailsRequestsByPath[normalizedMeetingPath];
        service.setBhgLoaderState(loaderContainer, false);
      }
    },

    flattenSearchData(data = []) {
      const items = [];

      const walkDestination = (node, parentId = "", ancestors = []) => {
        if (!node?.id) {
          return;
        }

        items.push({
          id: node.id,
          i18nTitle: node.i18nTitle,
          i18nTitleNormalized: normalizeForSearch(node.i18nTitle),
          path: node.path,
          pathNormalized: normalizeForSearch(node.path),
          levelType: node.levelType,
          parentId,
          ancestors,
          _id: node.id,
          _kind: "destination",
        });

        const nextAncestors = [...ancestors, node.id];

        (node.hotels || []).forEach((hotel, hotelIndex) => {
          items.push({
            ...hotel,
            hotelNameNormalized: normalizeForSearch(hotel?.hotelName),
            pathNormalized: normalizeForSearch(hotel?.path),
            parentId: node.id,
            ancestors: nextAncestors,
            _id: host.getHotelNodeId(hotel, node.id, hotelIndex),
            _kind: "hotel",
            levelType: "Hotel",
          });
        });

        (node.subDestinations || []).forEach((child) => walkDestination(child, node.id, nextAncestors));
      };

      data.forEach((rootNode) => walkDestination(rootNode));
      return items;
    },

    buildSearchIndex(data = []) {
      const searchIndex = {
        hotelIdsByParent: {},
        subtreeDestinationIdsByParent: {},
        subtreeHotelIdsByParent: {},
        parentByDestinationId: {},
      };

      const walkDestination = (node, parentId = null) => {
        if (!node?.id) {
          return { allDestinationIds: [], allHotelIds: [] };
        }

        if (parentId) {
          searchIndex.parentByDestinationId[node.id] = parentId;
        }

        const hotelIds = (node.hotels || []).map((hotel, idx) => host.getHotelNodeId(hotel, node.id, idx));
        searchIndex.hotelIdsByParent[node.id] = hotelIds;

        const allDestIds = [node.id];
        const allHotelIds = [...hotelIds];

        (node.subDestinations || []).forEach((child) => {
          const { allDestinationIds, allHotelIds: childHotelIds } = walkDestination(child, node.id);
          allDestIds.push(...allDestinationIds);
          allHotelIds.push(...childHotelIds);
        });

        searchIndex.subtreeDestinationIdsByParent[node.id] = allDestIds.filter((id) => id !== node.id);
        searchIndex.subtreeHotelIdsByParent[node.id] = allHotelIds;

        return { allDestinationIds: allDestIds, allHotelIds };
      };

      data.forEach((rootNode) => walkDestination(rootNode));
      return searchIndex;
    },

    computeVisibleIds(fuseResults = []) {
      const visibleDestinations = new Set();
      const visibleHotels = new Set();
      const matchedHotelParents = new Set();
      const { searchIndex } = host.props;

      fuseResults.forEach(({ item }) => {
        if (!item) {
          return;
        }

        if (item._kind === "hotel") {
          visibleHotels.add(item._id);
          if (item.parentId) {
            matchedHotelParents.add(item.parentId);
            visibleDestinations.add(item.parentId);
          }
        } else {
          visibleDestinations.add(item._id);
          searchIndex?.subtreeDestinationIdsByParent?.[item._id]?.forEach((id) => visibleDestinations.add(id));
          searchIndex?.subtreeHotelIdsByParent?.[item._id]?.forEach((id) => visibleHotels.add(id));
        }

        item.ancestors?.forEach((ancestorId) => ancestorId && visibleDestinations.add(ancestorId));
      });

      matchedHotelParents.forEach((parentId) => {
        searchIndex?.hotelIdsByParent?.[parentId]?.forEach((hotelId) => visibleHotels.add(hotelId));
      });

      return { visibleDestinationIds: visibleDestinations, visibleHotelIds: visibleHotels };
    },

    buildScoreIndex(fuseResults, query) {
      const safeFuseResults = Array.isArray(fuseResults) ? fuseResults : [];
      const destinationScores = {};
      const hotelScores = {};

      safeFuseResults.forEach(({ item, score }) => {
        const normScore = score ?? Number.POSITIVE_INFINITY;
        const target = item._kind === "hotel" ? hotelScores : destinationScores;

        if (!(item._id in target) || normScore < target[item._id]) {
          target[item._id] = normScore;
        }
      });

      return { destinationScores, hotelScores, hasSearchTerm: Boolean(query) };
    },

    sortByScore: (items, scoreAccessor) =>
      items
        .map((item, index) => ({ item, index, score: scoreAccessor(item) ?? Number.POSITIVE_INFINITY }))
        .sort((a, b) => {
          if (a.score === b.score) {
            return a.index - b.index;
          }

          return a.score - b.score;
        })
        .map(({ item }) => item),

    buildFilteredHotelsForNode(node, hasSearchTerm, visibleState, hotelScores) {
      return (node.hotels || [])
        .map((hotel, idx) => {
          const hotelId = host.getHotelNodeId(hotel, node.id, idx);
          if (hasSearchTerm && !visibleState.visibleHotelIds.has(hotelId)) {
            return null;
          }

          return {
            ...hotel,
            _searchScore: hotelScores[hotelId] ?? null,
          };
        })
        .filter(Boolean);
    },

    shouldSkipDestinationNode({ nodeId, hasSearchTerm, visibleState, subDestinations, hotels }) {
      return hasSearchTerm && !visibleState.visibleDestinationIds.has(nodeId) && !subDestinations.length && !hotels.length;
    },

    getBestScoreInCollection(arr, key) {
      return arr.reduce((best, curr) => Math.min(best, curr[key] ?? Number.POSITIVE_INFINITY), Number.POSITIVE_INFINITY);
    },

    resolveDestinationNodeScore({ nodeId, destinationScores, subDestinations, hotels }) {
      const childBestScore = service.getBestScoreInCollection(subDestinations, "_resolvedScore");
      const hotelBestScore = service.getBestScoreInCollection(hotels, "_searchScore");
      const ownScore = destinationScores[nodeId];
      const resolvedScore = [ownScore, childBestScore, hotelBestScore].filter(Number.isFinite);

      return resolvedScore.length ? Math.min(...resolvedScore) : null;
    },

    cloneDestinationForFilteredTree({ node, hasSearchTerm, visibleState, hotelScores, destinationScores }) {
      if (!node?.id) {
        return null;
      }

      const subDestinations = (node.subDestinations || [])
        .map((child) =>
          service.cloneDestinationForFilteredTree({
            node: child,
            hasSearchTerm,
            visibleState,
            hotelScores,
            destinationScores,
          }),
        )
        .filter(Boolean);

      const hotels = service.buildFilteredHotelsForNode(node, hasSearchTerm, visibleState, hotelScores);
      if (service.shouldSkipDestinationNode({ nodeId: node.id, hasSearchTerm, visibleState, subDestinations, hotels })) {
        return null;
      }

      return {
        ...node,
        subDestinations: service.sortByScore(subDestinations, (child) => child._resolvedScore),
        hotels: service.sortByScore(hotels, (hotel) => hotel._searchScore),
        _resolvedScore: service.resolveDestinationNodeScore({ nodeId: node.id, destinationScores, subDestinations, hotels }),
      };
    },

    buildFilteredTree(tree, visibleState, scoreIndex) {
      const safeTree = Array.isArray(tree) ? tree : [];
      const { hasSearchTerm, hotelScores, destinationScores } = scoreIndex;

      const sortedRoots = service.sortByScore(
        safeTree
          .map((rootNode) =>
            service.cloneDestinationForFilteredTree({
              node: rootNode,
              hasSearchTerm,
              visibleState,
              hotelScores,
              destinationScores,
            }),
          )
          .filter(Boolean),
        (root) => root._resolvedScore,
      );

      return sortedRoots.map(service.stripSearchMetadata);
    },

    stripSearchMetadata(node) {
      if (!node) {
        return null;
      }

      const { ...cleanNode } = node;

      cleanNode.hotels = (cleanNode.hotels || []).map(({ ...hotel }) => hotel);
      cleanNode.subDestinations = (cleanNode.subDestinations || []).map(service.stripSearchMetadata);

      return cleanNode;
    },

    highlightTextByMatches(text, matches, keyName) {
      const safeText = String(text || "");
      const safeMatches = Array.isArray(matches) ? matches : [];

      if (!safeMatches.length) {
        return escapeHtml(safeText);
      }

      const allowedKeys = keyName ? new Set([keyName, `${keyName}Normalized`]) : null;
      const relevantIndices = safeMatches.filter((m) => !allowedKeys || allowedKeys.has(m.key)).flatMap((m) => m.indices || []);

      const mergedIndices = mergeIntervals(relevantIndices);
      if (!mergedIndices.length) {
        return escapeHtml(safeText);
      }

      let cursor = 0;
      let highlighted = "";

      mergedIndices.forEach(([start, end]) => {
        if (end < cursor) {
          return;
        }
        const normStart = Math.max(start, cursor);

        if (normStart > cursor) {
          highlighted += escapeHtml(safeText.slice(cursor, normStart));
        }
        highlighted += `<strong>${escapeHtml(safeText.slice(normStart, end + 1))}</strong>`;
        cursor = end + 1;
      });

      if (cursor < safeText.length) {
        highlighted += escapeHtml(safeText.slice(cursor));
      }

      return highlighted;
    },

    initFuseSearch() {
      const { data, metadata } = host.props.searchData || {};
      if (!data || !metadata?.searchConfig?.keys) {
        return;
      }

      host.props.sourceTree = data;
      host.props.searchIndex = service.buildSearchIndex(data);

      const fuseOptions = {
        threshold: metadata.searchConfig.threshold ?? 0.3,
        keys: service.buildFuseKeysWithNormalizedVariants(metadata.searchConfig.keys),
        includeScore: true,
        includeMatches: true,
      };

      host.props.fuse = new Fuse(service.flattenSearchData(data), fuseOptions);
    },

    getEmptySearchResult(sourceTree) {
      return {
        results: [],
        tree: sourceTree,
        visibleDestinationIds: [],
        visibleHotelIds: [],
      };
    },

    buildTokenSearchResults(fuse, normalizedQuery) {
      return service.getSearchTokens(normalizedQuery).flatMap((token, tokenOrder) => fuse.search(token).map((result) => ({ ...result, _tokenOrder: tokenOrder })));
    },

    buildFuseSearchResults(fuse, normalizedQuery) {
      const primaryResults = fuse.search(normalizedQuery);
      const tokenResults = service.buildTokenSearchResults(fuse, normalizedQuery);
      return service.mergeSearchResults(primaryResults, tokenResults);
    },

    mapSearchResultWithHighlight(result) {
      const isHotel = result.item._kind === "hotel";
      const label = isHotel ? result.item.hotelName : result.item.i18nTitle;

      return {
        ...result,
        item: {
          ...result.item,
          highlightedLabel: service.highlightTextByMatches(label, result.matches, isHotel ? "hotelName" : "i18nTitle"),
        },
      };
    },

    buildSearchResponse({ fuseResults, sourceTree, rawQuery }) {
      const visibleState = service.computeVisibleIds(fuseResults);
      const scoreIndex = service.buildScoreIndex(fuseResults, rawQuery);

      return {
        results: fuseResults.map((result) => service.mapSearchResultWithHighlight(result)),
        tree: service.buildFilteredTree(sourceTree, visibleState, scoreIndex),
        visibleDestinationIds: [...visibleState.visibleDestinationIds],
        visibleHotelIds: [...visibleState.visibleHotelIds],
      };
    },

    canUseClientStorage() {
      try {
        return globalThis.localStorage !== undefined;
      } catch {
        return false;
      }
    },

    getSearchDataCacheKey() {
      const locale = document.documentElement.lang || "global";
      return `${SEARCH_DATA_CACHE_KEY_PREFIX}:store:${locale.toLowerCase()}`;
    },

    isSearchDataPayloadValid(payload) {
      return Boolean(payload && typeof payload === "object" && Array.isArray(payload.data));
    },

    readSearchDataFromClientCache() {
      if (!service.canUseClientStorage()) {
        return null;
      }

      const cacheKey = service.getSearchDataCacheKey();

      try {
        const rawCacheEntry = globalThis.localStorage.getItem(cacheKey);
        if (!rawCacheEntry) {
          return null;
        }

        const parsedCacheEntry = JSON.parse(rawCacheEntry);
        const cachedAt = Number(parsedCacheEntry?.cachedAt || 0);
        const payload = parsedCacheEntry?.payload;

        const isExpired = !Number.isFinite(cachedAt) || Date.now() - cachedAt > SEARCH_DATA_CACHE_TTL_MS;
        if (isExpired || !service.isSearchDataPayloadValid(payload)) {
          globalThis.localStorage.removeItem(cacheKey);
          return null;
        }

        return payload;
      } catch {
        try {
          globalThis.localStorage.removeItem(cacheKey);
        } catch {
          // Ignore storage cleanup failures.
        }
        return null;
      }
    },

    writeSearchDataToClientCache(payload) {
      if (!service.canUseClientStorage() || !service.isSearchDataPayloadValid(payload)) {
        return;
      }

      const cacheKey = service.getSearchDataCacheKey();

      try {
        const cacheEntry = {
          cachedAt: Date.now(),
          payload,
        };

        globalThis.localStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
      } catch {
        // Ignore storage write failures (quota/private mode).
      }
    },

    hydrateSearchStateFromPayload(payload) {
      const treeData = Array.isArray(payload.data) ? payload.data : [];
      Object.assign(host.props, {
        searchData: payload,
        sourceTree: treeData,
        filteredTree: treeData,
        selectedDestinationId: null,
      });
    },

    initializeSearchUiFromState(fastbooking) {
      service.initFuseSearch();
      host.renderTreeFromSearchData(fastbooking);
      host.manageDestinationPopover(fastbooking);
    },

    doSearch(query) {
      const trimmedQuery = query?.trim();
      const { fuse, sourceTree } = host.props;

      if (!fuse || !trimmedQuery) {
        return service.getEmptySearchResult(sourceTree);
      }

      const normalizedQuery = normalizeForSearch(trimmedQuery);
      if (!normalizedQuery) {
        return service.getEmptySearchResult(sourceTree);
      }

      const fuseResults = service.buildFuseSearchResults(fuse, normalizedQuery);
      return service.buildSearchResponse({
        fuseResults,
        sourceTree,
        rawQuery: trimmedQuery,
      });
    },

    async fetchSearchData(fastbooking) {
      const componentPath = fastbooking?.dataset?.path;
      if (!componentPath) {
        return null;
      }

      const cachedPayload = service.readSearchDataFromClientCache();
      if (cachedPayload) {
        service.hydrateSearchStateFromPayload(cachedPayload);
        service.initializeSearchUiFromState(fastbooking);
        return host.props.searchData;
      }

      const loaderContainer = service.resolveLoaderContainer(fastbooking);
      service.setBhgLoaderState(loaderContainer, true);

      try {
        const payload = await service.createTimedAjaxRequestPromise({
          requestUrl: componentPath,
          timeoutMs: 10000,
          timeoutErrorMessage: "Timeout: La petición tardó demasiado o falló silenciosamente.",
          emptyResponseErrorMessage: "Empty search data response",
        });

        if (!payload || typeof payload !== "object") {
          throw new Error("Search data payload is not an object");
        }

        service.hydrateSearchStateFromPayload(payload);
        service.writeSearchDataToClientCache(payload);
        service.initializeSearchUiFromState(fastbooking);

        return host.props.searchData;
      } catch (error) {
        console.error("Error fetching search data:", error);
        return null;
      } finally {
        service.setBhgLoaderState(loaderContainer, false);
      }
    },
  };

  return service;
};

export default createDataSearchService;
